import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Col, Form, Row } from 'antd';

import { convertDateStringtoMoment } from '@/utilz';
import DatePicker from '@/views/components/Inputs/DatePicker';

import Button from '../../../../../../../components/Buttons/Button';
import EmptyRecords from '../../../../../../../components/Common/EmptyRecords';
import FormInput from '../../../../../../../components/Inputs/FormInput';
import CommonModal from '../../../../../../../components/Modals/CommonModal';
import Paper from '../../../../../../../components/Paper/Paper';

const ITEMS_VIRTUALIZATION_BREAKPOINT = 100;
const ITEM_CARD_HEIGHT = 200;
const DEFAULT_CONTAINER_HEIGHT = 600;

const OverChargeUploadItemsModal = ({ open, setOpen, items = [], handleApply }) => {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(DEFAULT_CONTAINER_HEIGHT);

  const processedItems = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }
    return items.map((row) => ({
      ...row,
      shipDate: convertDateStringtoMoment(`${row.shipDate}Z`),
    }));
  }, [items]);

  useEffect(() => {
    if (processedItems.length > 0) {
      form.setFieldValue('items', processedItems);
    }
  }, [processedItems, form]);

  useEffect(() => {
    if (open && containerRef.current) {
      const updateHeight = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerHeight(rect.height || DEFAULT_CONTAINER_HEIGHT);
        }
      };
      requestAnimationFrame(updateHeight);
      window.addEventListener('resize', updateHeight);
      return () => {
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, [open]);

  const handleFormSubmit = useCallback(
    (data) => {
      handleApply(data.items);
    },
    [handleApply],
  );

  const shouldVirtualize = items.length > ITEMS_VIRTUALIZATION_BREAKPOINT;

  return (
    // biome-ignore lint/complexity/noUselessFragments: placeholder
    <CommonModal className="w-95" open={open} title="Items Details" onClose={() => setOpen(false)} customFooter={<></>}>
      <Row className="h-100 overflow-hidden flex-direction-column">
        <Col
          ref={containerRef}
          className={`flex-1 pb-30 ${shouldVirtualize ? '' : 'custom-scroll-bar'}`}
          style={{ minHeight: 0, overflow: shouldVirtualize ? 'hidden' : 'auto' }}
        >
          {items?.length === 0 ? (
            <EmptyRecords title="No items found!" descriptionOne="" descriptionTwo="" />
          ) : (
            <Form onFinish={handleFormSubmit} form={form}>
              <Form.List name="items">
                {(formItems) => {
                  if (shouldVirtualize) {
                    return (
                      <div style={{ height: containerHeight }}>
                        <Virtuoso
                          style={{ height: '100%' }}
                          totalCount={formItems.length}
                          itemContent={(index) => <ItemCard item={formItems[index]} key={formItems[index].name} />}
                          defaultItemHeight={ITEM_CARD_HEIGHT}
                        />
                      </div>
                    );
                  }
                  return formItems.map((item) => <ItemCard item={item} key={item.name} />);
                }}
              </Form.List>
            </Form>
          )}
        </Col>
        <Col>
          <Row justify="end" gutter={15}>
            <Col>
              <Button onClick={() => form.submit()}>Save</Button>
            </Col>
            <Col>
              <Button type="default" variant="danger" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </CommonModal>
  );
};

export default OverChargeUploadItemsModal;

const ItemCard = memo(({ item }) => {
  return (
    <Paper className="mb-15 px-15 pt-15" border>
      <Row gutter={10}>
        <Col sm={3}>
          <FormInput
            label="Freight Bill / Pro Number"
            placeholder="Enter Freight Bill / Pro Number"
            margin="mb-0"
            name={[item.key, 'proNo']}
            editMode
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              {
                message: 'Please enter Freight Bill / Pro Number',
                required: true,
              },
            ]}
            maxLength={25}
          />
        </Col>
        <Col sm={3}>
          <DatePicker
            label="Ship Date"
            placeholder="Choose ship date"
            name={[item.key, 'shipDate']}
            editMode
            margin="m-0"
            rules={[
              {
                message: 'Please choose ship date',
                required: true,
              },
            ]}
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Package Number"
            placeholder="Please enter package number"
            name={[item.key, 'packageNumber']}
            editMode
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Invoice Number"
            placeholder="Please enter invoice number"
            name={[item.key, 'invoiceNumber']}
            editMode
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Chargeable Weight"
            placeholder="Please enter chargeable weight"
            name={[item.key, 'weight']}
            editMode
            inputType="number"
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Billed Amount"
            placeholder="Please enter billed amount"
            name={[item.key, 'billedAmount']}
            editMode
            inputType="number"
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Paid Amount"
            placeholder="Please enter paid amount"
            name={[item.key, 'paidAmount']}
            editMode
            inputType="number"
          />
        </Col>
        <Col sm={2}>
          <FormInput
            label="Correct Charges"
            placeholder="Please enter correct charges"
            name={[item.key, 'correctCharges']}
            editMode
            inputType="number"
          />
        </Col>
        <Col sm={3}>
          <FormInput
            label="Overcharge Sum"
            placeholder="Please enter overcharge sum"
            name={[item.key, 'overchargeAmount']}
            editMode={false}
            inputType="number"
            margin="mb-15"
          />
        </Col>
        <Col sm={3}>
          <FormInput
            label="Recovery Sum"
            placeholder="Please enter recovery sum"
            name={[item.key, 'recoveryAmount']}
            editMode
            inputType="number"
          />
        </Col>
      </Row>
    </Paper>
  );
});

ItemCard.displayName = 'ItemCard';
